import React from 'react';

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import {
  PromoProductTypes,
  getMinSpendProductTypeOptions,
  getProductTypeOptions,
  getVoucherProductTypeOptions,
} from '~/services/PromoService';

// Standalone MultiProductOptionSelect for modal use
interface StandaloneMultiProductOptionSelectProps {
  id: string;
  value: Array<string>;
  defaultValue?: Array<string>;
  filterFields?: Array<string>;
  showUpToXProducts?: number;
  promoProductTypes: PromoProductTypes;
  multiSelectType: 'products' | 'min_spend_products';
  productContext?: 'promo' | 'voucher';
  disabled?: boolean;
  onChange: (value: Array<string>) => void;
}

function StandaloneMultiProductOptionSelect({
  id,
  value,
  defaultValue,
  filterFields,
  showUpToXProducts = 2,
  promoProductTypes,
  multiSelectType,
  productContext = 'promo',
  disabled = false,
  onChange,
}: StandaloneMultiProductOptionSelectProps) {
  // Use defaultValue when value is empty or undefined, fallback to empty array
  const effectiveValue = value && value.length > 0 ? value : defaultValue || [];

  const getProductOptions = productContext === 'voucher' ? getVoucherProductTypeOptions : getProductTypeOptions;

  const productOptions =
    multiSelectType == 'products'
      ? getProductOptions(promoProductTypes, filterFields)
      : getMinSpendProductTypeOptions(promoProductTypes, filterFields);

  const productTypeOptions = [...productOptions]
    .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label))
    .map((opt) => ({
      isChecked: effectiveValue.includes(opt.value),
      label: opt.label,
      value: opt.value,
    }));

  const renderValue = (selected: Array<string>) => {
    if (selected.length > showUpToXProducts) {
      return `${selected.length} Products Selected`;
    }
    return selected
      .map((selectedValue) => productTypeOptions.find((opt) => opt.value === selectedValue)?.label ?? selectedValue)
      .join(', ');
  };

  const handleChange = (event: SelectChangeEvent<Array<string>>) => {
    const newValue = event.target.value;
    onChange(newValue as Array<string>);
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id={`${multiSelectType}-label`}>Products</InputLabel>
      <Select
        labelId={`${multiSelectType}-label`}
        id={id}
        title={multiSelectType === 'products' ? 'Products' : 'Min Spend Products'}
        multiple
        value={effectiveValue}
        onChange={handleChange}
        renderValue={(value: unknown) => {
          if (Array.isArray(value)) {
            return renderValue(value);
          }
          return '';
        }}
        label={multiSelectType === 'products' ? 'Select Products' : 'Select Min Spend Products'}
      >
        {productTypeOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={opt.isChecked} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default StandaloneMultiProductOptionSelect;
